// What happens after a season ends.
//
// Three described choices, not a redirect loop. A member says what they want,
// the host sees the tally and decides. The vote informs the call rather than
// making it: somebody still has to run the thing.

export type NextChoice = "run_it_back" | "change_it_up" | "good_for_now";

export interface NextOption {
  key: NextChoice;
  title: string;
  blurb: string;
}

export const NEXT_OPTIONS: NextOption[] = [
  {
    key: "run_it_back",
    title: "Run it back",
    blurb: "Same crew, same setup, go again.",
  },
  {
    key: "change_it_up",
    title: "Change it up",
    blurb: "Another season, but switch something. New settings, or new people.",
  },
  {
    key: "good_for_now",
    title: "We're good for now",
    blurb: "Wrap it here. You can always start another one later.",
  },
];

// What to call the season that would come next.
//
// Never guess a number. A wrong "Season 4" in front of a crew that has played
// twice is worse than not naming it at all, so anything the app cannot work out
// from real history gets the honest phrasing instead.
export function nextSeasonLabel(seasonsPlayed: number | null | undefined): string {
  if (typeof seasonsPlayed !== "number" || !Number.isFinite(seasonsPlayed) || seasonsPlayed < 1) {
    return "another season";
  }
  return `Season ${seasonsPlayed + 1}`;
}

export interface VoteTally {
  run_it_back: number;
  change_it_up: number;
  good_for_now: number;
  voted: number;
  members: number;
}

// What the host reads off the tally, in a sentence rather than a bar chart.
//
// Every count is treated as maybe-missing. A tally that arrives without its
// totals (an older server, a partial response) used to print "undefined of
// undefined want to wrap it, NaN would keep going" straight to the host at the
// end of their season. Counting what is there is always better than that.
export function readTally(raw: Partial<VoteTally> | null | undefined): { headline: string; detail: string } {
  const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0);
  const runItBack = n(raw?.run_it_back);
  const changeItUp = n(raw?.change_it_up);
  const goodForNow = n(raw?.good_for_now);
  const cast = runItBack + changeItUp + goodForNow;
  const t = {
    run_it_back: runItBack,
    change_it_up: changeItUp,
    good_for_now: goodForNow,
    // Missing totals are derived rather than guessed: the votes themselves say
    // how many people answered.
    voted: Math.max(n(raw?.voted), cast),
    members: Math.max(n(raw?.members), n(raw?.voted), cast),
  };

  if (t.voted === 0) {
    return {
      headline: "Nobody's said yet",
      detail: `Waiting on ${t.members} ${t.members === 1 ? "person" : "people"}.`,
    };
  }
  const wantMore = t.run_it_back + t.change_it_up;
  const waiting = Math.max(0, t.members - t.voted);
  const tail = waiting > 0 ? ` Still waiting on ${waiting}.` : "";

  if (wantMore === 0) {
    return {
      headline: "Everybody's good for now",
      detail: `All ${t.voted} who answered want to wrap it here.${tail}`,
    };
  }
  if (t.run_it_back >= t.change_it_up && t.run_it_back >= t.good_for_now) {
    return {
      headline: "They want to run it back",
      detail: `${t.run_it_back} of ${t.voted} would go again as is.${tail}`,
    };
  }
  if (t.change_it_up >= t.good_for_now) {
    return {
      headline: "They want another one, with changes",
      detail: `${t.change_it_up} of ${t.voted} want something switched up first.${tail}`,
    };
  }
  return {
    headline: "Mostly good for now",
    detail: `${t.good_for_now} of ${t.voted} want to wrap it, ${wantMore} would keep going.${tail}`,
  };
}

// The message that goes to the real group chat. There is no chat in the app on
// purpose, so every share hands off to where the crew already talks.
export function shareText(kind: "settle" | "trip" | "champion", crewName: string, championName?: string): string {
  switch (kind) {
    case "champion":
      return championName
        ? `${championName} took it this season in ${crewName}. Standings are in the app.`
        : `${crewName} wrapped the season. Standings are in the app.`;
    case "settle":
      return `${crewName} season's done. Time to settle up.`;
    case "trip":
      return `${crewName} season's done. When are we actually linking up?`;
  }
}
